/**
 * Ref: https://tanstack.com/table/latest/docs/framework/react/examples/virtualized-infinite-scrolling
 */
import { closestCenter, DndContext, type DragEndEvent, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import type { ColumnDef, OnChangeFn, SortingState } from '@tanstack/react-table';
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import cx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SWRResponse } from 'swr';
import type { SWRInfiniteResponse } from 'swr/infinite';
import { IError, INoResults } from 'ui/component/Icons.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { ControlledCheckbox } from 'ui/control/ControlledCheckbox.tsx';
import { ReorderableTableBody, StandardTableBody, TableHeader, TablePagination } from './index.ts';

export const DEFAULT_PAGE_SIZE = 50;

type Props = {
  className?: string;
  estimateSize: number | ((index: number) => number);
  overscan?: number;
  columns: ColumnDef<any>[];
  defaultSorting?: SortingState;
  swr?: SWRInfiniteResponse | SWRResponse;
  items?: any[];
  emptyState?: React.ReactNode;
  pagination?: boolean;
  rowsCount?: number;
  onRowClick?: (data: any, event?: React.MouseEvent) => void;
  onRowMouseEnter?: (data: any, event?: React.MouseEvent) => void;
  onRowMouseLeave?: (data: any, event?: React.MouseEvent) => void;
  onRowSelect?: (selectedRows: any[]) => void;
  onColumnSort?: (data: any) => void;
  wrapHeaders?: boolean;
  onReorder?: (newOrder: any[]) => void;
  initialOrder?: string[];
  columnVisibility?: Record<string, boolean>;
  scrollRef?: React.RefCallback<HTMLDivElement>;
};

export type TableRef = {
  clearSelection: () => void;
  toggleAllRowsSelected: (value?: boolean) => void;
};

// use this so it's immutable or an infinite render will be caused by the data: data||[]
const NO_DATA = [];

const selectionColumn = {
  id: 'select',
  size: 40,
  header: ({ table }) => (
    <ControlledCheckbox type="checkbox" checked={table.getIsAllRowsSelected()} indeterminate={table.getIsSomeRowsSelected()} onChange={table.getToggleAllRowsSelectedHandler()} />
  ),
  cell: ({ row }) => (
    <ControlledCheckbox type={'checkbox'} data-table-no-row-events checked={row.getIsSelected()} disabled={!row.getCanSelect()} onChange={row.getToggleSelectedHandler()} />
  ),
  meta: { hideFallback: true },
};

export const Table = React.forwardRef<TableRef, Props>(
  (
    {
      className,
      columns,
      swr,
      items,
      emptyState,
      pagination = false,
      rowsCount,
      estimateSize,
      defaultSorting = [],
      overscan,
      onRowClick,
      onRowMouseEnter,
      onRowMouseLeave,
      onRowSelect,
      onColumnSort,
      wrapHeaders = false,
      onReorder,
      columnVisibility = {},
      scrollRef,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [sorting, setSorting] = React.useState<SortingState>(defaultSorting);
    const tableContainerRef = React.useRef<HTMLDivElement>(null);

    const combinedRef = React.useCallback(
      (el: HTMLDivElement | null) => {
        tableContainerRef.current = el;
        scrollRef?.(el);
      },
      [scrollRef],
    );

    /**
     * We have to make a distinction between the useSWR and useSWRInfinite hooks to properly:
     * 1) data: flat array (results) vs array of arrays (pages of results)
     * 2) pagination: hide "next page" button vs show "next page" button
     */
    const {
      data: datasetOrPages = NO_DATA,
      isValidating,
      error,
      size,
      setSize,
    } = swr
      ? (swr as SWRInfiniteResponse)
      : items
        ? { data: items, isValidating: false, error: null, size: items.length, setSize: undefined }
        : { data: NO_DATA, isValidating: false, error: null, size: 0, setSize: undefined };

    const rawData: any[] = React.useMemo(() => datasetOrPages?.flat() ?? NO_DATA, [datasetOrPages]);

    // Derive page size from the first page of data, or default to 50
    const pageSize = React.useMemo(() => {
      if (Array.isArray(datasetOrPages) && datasetOrPages.length > 0 && Array.isArray(datasetOrPages[0])) {
        return datasetOrPages[0].length || DEFAULT_PAGE_SIZE;
      }
      return DEFAULT_PAGE_SIZE;
    }, [datasetOrPages]);

    /*
    if (pagination && rawData.length !== new Set(rawData.map((item) => item.id)).size) {
      console.error('Pagination is faulty: duplicated or missing items were found in data array.');
    }
    */

    const fetchNextPage = React.useCallback(() => {
      if (!setSize) return Function.prototype;
      return setSize(size + 1);
    }, [size, setSize]);

    // Keep track of ordered data
    const [orderedData, setOrderedData] = React.useState<any[]>([]);

    // Update ordered data when raw data changes
    React.useEffect(() => {
      if (!isValidating) {
        setOrderedData(rawData);
      }
    }, [rawData, isValidating]);

    // Setup DnD sensors
    const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));

    // Drag end handler
    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = orderedData.findIndex((item) => item.id === active.id);
      const newIndex = orderedData.findIndex((item) => item.id === over.id);

      const newOrderedData = arrayMove([...orderedData], oldIndex, newIndex);
      setOrderedData(newOrderedData);

      if (onReorder) {
        onReorder(newOrderedData);
      }
    };

    // Use ordered data only when reordering is enabled and we have data
    const tableData = onReorder && orderedData.length > 0 ? orderedData : rawData;

    const columnsWithSelection = React.useMemo(() => {
      let base = columns;
      if (onRowSelect) base = [selectionColumn, ...base];
      return base;
    }, [onRowSelect, columns]);

    const onSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
      const nextSorting: SortingState = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
      setSorting(nextSorting);
      if (onColumnSort) onColumnSort(nextSorting);
    };

    const table = useReactTable({
      data: tableData,
      columns: columnsWithSelection as ColumnDef<any, any>[],
      state: { sorting },
      manualSorting: setSize !== undefined,
      enableRowSelection: !!onRowSelect,
      onSortingChange,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getRowId: (row) => row.id, //required because row indexes will change
      initialState: { columnVisibility },
    });

    const { rows } = table.getRowModel();
    const { rows: selectedRows } = table.getSelectedRowModel();

    const virtualizer = useVirtualizer({
      count: rows.length,
      getScrollElement: () => tableContainerRef.current,
      estimateSize: typeof estimateSize === 'number' ? () => estimateSize : estimateSize,
      overscan,
    });

    const virtualRows = virtualizer.getVirtualItems();

    const onRowClickHandler = React.useCallback(
      (row) => (event: React.MouseEvent) => {
        if (!onRowClick) return;
        else if ((event.target as HTMLElement).hasAttribute('data-table-no-row-events')) return event.stopPropagation();
        else onRowClick(row.original, event);
      },
      [onRowClick],
    );

    const onRowMouseEnterHandler = React.useCallback(
      (row) => (event: React.MouseEvent) => {
        if (!onRowMouseEnter) return;
        else if ((event.target as HTMLElement).hasAttribute('data-table-no-row-events')) return event.stopPropagation();
        onRowMouseEnter(row.original, event);
      },
      [onRowMouseEnter],
    );

    const onRowMouseLeaveHandler = React.useCallback(
      (row) => (event: React.MouseEvent) => {
        if (!onRowMouseLeave) return;
        else if ((event.target as HTMLElement).hasAttribute('data-table-no-row-events')) return event.stopPropagation();
        onRowMouseLeave(row.original, event);
      },
      [onRowMouseLeave],
    );

    React.useEffect(() => {
      if (onRowSelect) {
        onRowSelect(selectedRows.map((row) => row.original));
      }
    }, [selectedRows, onRowSelect]);

    // Expose table methods via ref
    React.useImperativeHandle(
      ref,
      () => ({
        clearSelection: () => table.toggleAllRowsSelected(false),
        toggleAllRowsSelected: (value?: boolean) => table.toggleAllRowsSelected(value),
      }),
      [table],
    );

    const totalPages = rowsCount ? Math.ceil(rowsCount / pageSize) : 0;

    const loadAll = React.useCallback(() => {
      if (totalPages > 0) {
        setSize?.(totalPages);
      }
    }, [totalPages, setSize]);

    const isFirstLoading = !pagination ? isValidating || tableData === NO_DATA : isValidating && size <= 1;
    const isRefetchLoading = isValidating && (!size || size > 1); // if we have pagination, don't flicker when loading new page
    const isErrored = !isValidating && !!error;
    const noResults = !isValidating && !isErrored && tableData.length === 0;

    if (isFirstLoading) {
      return (
        <div className={cx(className, 'flex items-center justify-center')}>
          <div className="flex w-max flex-col items-center justify-center p-12">
            <Spinner />
          </div>
        </div>
      );
    }

    if (noResults) {
      return (
        <div className={cx(className, 'flex items-center justify-center')}>
          <div className="flex w-max flex-col items-center justify-center p-12">
            {emptyState ?? (
              <>
                <INoResults size="48" />
                <p>{t('table.noResults')}</p>
              </>
            )}
          </div>
        </div>
      );
    }

    if (isErrored) {
      return (
        <div className={cx(className, 'flex items-center justify-center')}>
          <div className="flex w-max flex-col items-center justify-center p-12">
            <IError size="48" />
            <p title={t('table.error.tooltip', { message: error?.response?.data?.message ?? error?.message })}>{t('table.error.title')}</p>
          </div>
        </div>
      );
    }

    const renderTable = () => (
      <div className={cx('relative overflow-auto border-b border-b-grey-border bg-white', className)} ref={combinedRef}>
        <table data-role="table" className="w-full table-fixed">
          <TableHeader table={table} wrapHeaders={wrapHeaders} onReorder={onReorder} />
          {onReorder ? (
            <ReorderableTableBody rows={rows} tableData={tableData} />
          ) : (
            <StandardTableBody
              virtualizer={virtualizer}
              virtualRows={virtualRows}
              rows={rows}
              onRowClickHandler={onRowClickHandler}
              onRowMouseEnterHandler={onRowMouseEnterHandler}
              onRowMouseLeaveHandler={onRowMouseLeaveHandler}
            />
          )}
        </table>
      </div>
    );

    return (
      <>
        {onReorder && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
            {renderTable()}
          </DndContext>
        )}
        {!onReorder && renderTable()}
        <TablePagination
          isErrored={isErrored}
          rowsCount={rowsCount}
          tableData={tableData}
          pagination={pagination}
          isValidating={isValidating}
          isRefetchLoading={isRefetchLoading}
          fetchNextPage={fetchNextPage}
          fetchAll={loadAll}
          t={t}
          rows={rows}
        />
      </>
    );
  },
);

Table.displayName = 'Table';
